import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const MenuSuggestionCard = ({ menu, onSave }) => {
  const [activeTab, setActiveTab] = useState('menu');

  if (!menu) return null;

  const tabs = [
    { key: 'menu', label: 'Menu', icon: 'Restaurant' },
    { key: 'timeline', label: 'Timeline', icon: 'Clock' },
    { key: 'tips', label: 'Tips', icon: 'Lightbulb' }
  ];

  return (
    <div className="bg-card rounded-lg border overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b bg-muted/30">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-semibold text-foreground mb-2">
              Dinner Party Menu
            </h3>
            <p className="text-muted-foreground">
              Complete menu with cooking timeline and expert tips
            </p>
          </div>
          <Button onClick={() => onSave?.(menu)}>
            <Icon name="Bookmark" size={16} className="mr-2" />
            Save Menu
          </Button>
        </div>
      </div>
      {/* Tabs */}
      <div className="flex border-b">
        {tabs?.map((tab) => (
          <button
            key={tab?.key}
            onClick={() => setActiveTab(tab?.key)}
            className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === tab?.key
                ? 'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name={tab?.icon} size={16} className="mr-2 inline" />
            {tab?.label}
          </button>
        ))}
      </div>
      {/* Content */}
      <div className="p-6">
        {activeTab === 'menu' && (
          <div className="space-y-6">
            {/* Appetizers */}
            {menu?.menu?.appetizers && (
              <div>
                <h4 className="font-semibold text-foreground mb-3 flex items-center">
                  <Icon name="Utensils" size={16} className="mr-2 text-primary" />
                  Appetizers
                </h4>
                <div className="grid gap-2">
                  {menu?.menu?.appetizers?.map((item, index) => (
                    <div key={index} className="flex items-center p-3 bg-muted/50 rounded-lg">
                      <Icon name="Circle" size={8} className="mr-3 text-primary" />
                      <span className="text-foreground">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Main Course */}
            {menu?.menu?.mainCourse && (
              <div>
                <h4 className="font-semibold text-foreground mb-3 flex items-center">
                  <Icon name="ChefHat" size={16} className="mr-2 text-primary" />
                  Main Course
                </h4>
                <div className="p-4 bg-primary/5 rounded-lg border-l-4 border-primary">
                  <span className="text-foreground font-medium">{menu?.menu?.mainCourse}</span>
                </div>
              </div>
            )}

            {/* Sides */}
            {menu?.menu?.sides && (
              <div>
                <h4 className="font-semibold text-foreground mb-3 flex items-center">
                  <Icon name="Plus" size={16} className="mr-2 text-primary" />
                  Side Dishes
                </h4>
                <div className="grid gap-2">
                  {menu?.menu?.sides?.map((item, index) => (
                    <div key={index} className="flex items-center p-3 bg-muted/50 rounded-lg">
                      <Icon name="Circle" size={8} className="mr-3 text-primary" />
                      <span className="text-foreground">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Dessert */}
            {menu?.menu?.dessert && (
              <div>
                <h4 className="font-semibold text-foreground mb-3 flex items-center">
                  <Icon name="Cake" size={16} className="mr-2 text-primary" />
                  Dessert
                </h4>
                <div className="p-4 bg-muted/50 rounded-lg">
                  <span className="text-foreground">{menu?.menu?.dessert}</span>
                </div>
              </div>
            )}

            {/* Wines */}
            {menu?.menu?.wines && (
              <div>
                <h4 className="font-semibold text-foreground mb-3 flex items-center">
                  <Icon name="Wine" size={16} className="mr-2 text-primary" />
                  Wine Pairings
                </h4>
                <div className="grid gap-2">
                  {menu?.menu?.wines?.map((wine, index) => (
                    <div key={index} className="flex items-center p-3 bg-muted/50 rounded-lg">
                      <Icon name="Circle" size={8} className="mr-3 text-primary" />
                      <span className="text-foreground">{wine}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'timeline' && menu?.timeline && (
          <div className="space-y-6">
            {/* Day Before */}
            {menu?.timeline?.dayBefore && (
              <div>
                <h4 className="font-semibold text-foreground mb-3 flex items-center">
                  <Icon name="Calendar" size={16} className="mr-2 text-blue-600" />
                  Day Before
                </h4>
                <div className="space-y-2">
                  {menu?.timeline?.dayBefore?.map((task, index) => (
                    <div key={index} className="flex items-start p-3 bg-blue-50 rounded-lg">
                      <Icon name="CheckCircle" size={16} className="mr-3 text-blue-600 mt-0.5" />
                      <span className="text-foreground">{task}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Day Of */}
            {menu?.timeline?.dayOf && (
              <div>
                <h4 className="font-semibold text-foreground mb-3 flex items-center">
                  <Icon name="Sun" size={16} className="mr-2 text-orange-600" />
                  Day Of Party
                </h4>
                <div className="space-y-2">
                  {menu?.timeline?.dayOf?.map((task, index) => (
                    <div key={index} className="flex items-start p-3 bg-orange-50 rounded-lg">
                      <Icon name="CheckCircle" size={16} className="mr-3 text-orange-600 mt-0.5" />
                      <span className="text-foreground">{task}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Last Hour */}
            {menu?.timeline?.lastHour && (
              <div>
                <h4 className="font-semibold text-foreground mb-3 flex items-center">
                  <Icon name="Clock" size={16} className="mr-2 text-red-600" />
                  Final Hour
                </h4>
                <div className="space-y-2">
                  {menu?.timeline?.lastHour?.map((task, index) => (
                    <div key={index} className="flex items-start p-3 bg-red-50 rounded-lg">
                      <Icon name="CheckCircle" size={16} className="mr-3 text-red-600 mt-0.5" />
                      <span className="text-foreground">{task}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'tips' && menu?.tips && (
          <div className="space-y-4">
            <h4 className="font-semibold text-foreground mb-3 flex items-center">
              <Icon name="Lightbulb" size={16} className="mr-2 text-primary" />
              Expert Tips
            </h4>
            <div className="space-y-3">
              {menu?.tips?.map((tip, index) => (
                <div key={index} className="flex items-start p-4 bg-primary/5 rounded-lg border-l-4 border-primary">
                  <Icon name="Star" size={16} className="mr-3 text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-foreground leading-relaxed">{tip}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      {/* Footer Actions */}
      <div className="p-6 bg-muted/30 border-t">
        <div className="flex space-x-3">
          <Button variant="outline" className="flex-1">
            <Icon name="Download" size={16} className="mr-2" />
            Export Menu
          </Button>
          <Button variant="outline" className="flex-1">
            <Icon name="ShoppingCart" size={16} className="mr-2" />
            Generate Shopping List
          </Button>
          <Button className="flex-1">
            <Icon name="Calendar" size={16} className="mr-2" />
            Add to Event
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MenuSuggestionCard;